<template>
  <div class="modal-overlay" @click.self="close">
    <div class="modal-content">
      <div class="modal-header">
        <h2>{{ isEdit ? '编辑考试' : '添加考试' }}</h2>
        <button class="close-btn" @click="close">&times;</button>
      </div>

      <form @submit.prevent="submit">
        <!-- 第一部分：基本資訊 -->
        <div class="form-section">
          <h3 class="section-title">基本資訊</h3>
          
          <div class="form-group">
            <label for="name">考試名稱 *</label>
            <input id="name" v-model="form.name" required placeholder="例如：數學第一次段考">
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="authority">考試機構 *</label>
              <select id="authority" v-model="form.authority" required @change="handleAuthorityChange">
                <option v-for="option in authorityOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </div>

            <div class="form-group">
              <label for="type">考試類型 *</label>
              <select id="type" v-model="form.type" required>
                <option v-for="option in typeOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="subject">科目 *</label>
              <select id="subject" v-model="form.subject" required>
                <option v-for="option in subjectOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label for="grade">年級</label>
              <select id="grade" v-model="form.grade">
                <option value="">請選擇年級</option>
                <option v-for="grade in gradeOptions" :key="grade.value" :value="grade.value">
                  {{ grade.label }}
                </option>
              </select>
            </div>
            
            <div class="form-group">
              <label for="full_score">總分</label>
              <input 
                id="full_score" 
                type="number" 
                v-model="form.full_score" 
                min="0" 
                step="0.1"
                placeholder="預設為100"
              >
            </div>
          </div>

          <div class="form-group">
            <label for="exam_date">考試日期 *</label>
            <input 
              id="exam_date" 
              type="date" 
              v-model="form.exam_date" 
              required
            >
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="school_year">學年度</label>
              <input 
                id="school_year" 
                type="number" 
                v-model="form.school_year" 
                min="100" 
                max="200"
                placeholder="例如：114"
              >
            </div>

            <div class="form-group">
              <label for="semester">學期</label>
              <select id="semester" v-model="form.semester">
                <option value="">請選擇</option>
                <option value="1">第一學期</option>
                <option value="2">第二學期</option>
              </select>
            </div>
          </div>

          <div class="form-group">
            <label for="description">考試說明</label>
            <textarea 
              id="description" 
              v-model="form.description" 
              rows="3"
              placeholder="可選的考試說明..."
            ></textarea>
          </div>
        </div>

        <!-- 第二部分：補習班考試 - 選擇課程 -->
        <div class="form-section" v-if="form.authority === 'MACADEMY'">
          <h3 class="section-title">選擇課程</h3>
          
          <div class="form-group">
            <label for="course_id">選擇課程 *</label>
            <select 
              id="course_id" 
              v-model="selectedCourseId" 
              required 
              @change="handleCourseChange"
              :disabled="loadingCourses"
            >
              <option value="">請選擇課程</option>
              <option v-for="course in courses" :key="course.course_id" :value="course.course_id">
                {{ course.name }} - {{ course.subject }} ({{ course.grade }}年級)
              </option>
            </select>
            <p class="helper-text" v-if="loadingCourses">載入課程中...</p>
          </div>
        </div>

        <!-- 第三部分：補習班考試 - 選擇課程時段 -->
        <div class="form-section" v-if="form.authority === 'MACADEMY' && selectedCourseId">
          <h3 class="section-title">選擇課程時段</h3>
          
          <div class="form-group">
            <label for="session_id">選擇課程時段 *</label>
            <select 
              id="session_id" 
              v-model="form.session_id" 
              required
              :disabled="loadingSessions"
            >
              <option value="">請選擇課程時段</option>
              <option v-for="session in courseSessions" :key="session.session_id" :value="session.session_id">
                {{ formatSessionDate(session.start_at) }} - {{ session.school.name }}
              </option>
            </select>
            <p class="helper-text" v-if="loadingSessions">載入課程時段中...</p>
            <p class="helper-text" v-else-if="courseSessions.length > 0">預設選擇最新的課程時段</p>
          </div>

          <div class="form-group" v-if="!loadingSessions && courseSessions.length === 0">
            <p class="no-sessions-message">此課程目前沒有課程時段</p>
          </div>
        </div>

        <div class="form-actions">
          <button type="button" class="cancel-btn" @click="close">取消</button>
          <button type="submit" class="submit-btn" :disabled="loading">
            {{ loading ? '提交中...' : '提交' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useMutation, useQuery } from '@vue/apollo-composable';
import { CREATE_EXAM, UPDATE_EXAM } from '../graphql/queries';
import { GET_COURSES, GET_COURSE_SESSIONS } from '../graphql/queries';

const props = defineProps({
  exam: Object,
  isEdit: Boolean
});

const emit = defineEmits(['close', 'saved']);

// 載入狀態
const loading = ref(false);
const loadingCourses = ref(false);
const loadingSessions = ref(false);

// 選擇的課程 ID
const selectedCourseId = ref('');

// 選項配置
const authorityOptions = [
  { label: '學校', value: 'SCHOOL' },
  { label: '補習班', value: 'MACADEMY' },
  { label: '其他', value: 'OTHER' },
];

const typeOptions = [
  { label: '期中考', value: 'MIDTERM' },
  { label: '期末考', value: 'FINAL' },
  { label: '週考', value: 'WEEKLY' },
  { label: '第一次段考', value: 'FIRST_TERM' },
  { label: '第二次段考', value: 'SECOND_TERM' },
  { label: '第三次段考', value: 'THIRD_TERM' },
  { label: '隨堂考', value: 'QUIZ' },
  { label: '模擬考', value: 'MOCK' },
  { label: '月考', value: 'MONTHLY' },
  { label: '單元測驗', value: 'UNIT_TEST' },
  { label: '複習考', value: 'REVIEW_TEST' },
  { label: '分班測驗', value: 'PLACEMENT' },
  { label: '證照考試', value: 'CERTIFICATION' },
  { label: '競賽', value: 'COMPETITION' },
];

const subjectOptions = [
  { label: '國文', value: 'CHINESE' },
  { label: '國文作文', value: 'CHINESE_ESSAY' },
  { label: '英文', value: 'ENGLISH' },
  { label: '英文作文', value: 'ENGLISH_ESSAY' },
  { label: '英文聽力', value: 'ENGLISH_LISTENING' },
  { label: '數學', value: 'MATH' },
  { label: '生物', value: 'BIOLOGY' },
  { label: '理化', value: 'SCIENCE' },
  { label: '物理', value: 'PHYSICS' },
  { label: '化學', value: 'CHEMISTRY' },
  { label: '歷史', value: 'HISTORY' },
  { label: '地理', value: 'GEOGRAPHY' },
  { label: '公民', value: 'CIVICS' },
  { label: '其他', value: 'OTHER' },
];

const gradeOptions = [
  { label: '小一', value: 1 },
  { label: '小二', value: 2 },
  { label: '小三', value: 3 },
  { label: '小四', value: 4 },
  { label: '小五', value: 5 },
  { label: '小六', value: 6 },
  { label: '國一', value: 7 },
  { label: '國二', value: 8 },
  { label: '國三', value: 9 },
  { label: '高一', value: 10 },
  { label: '高二', value: 11 },
  { label: '高三', value: 12 }
];

// 表單資料
const defaultForm = {
  name: '',
  authority: 'SCHOOL',
  type: 'FIRST_TERM',
  subject: 'MATH',
  grade: null,
  exam_date: '',
  full_score: 100,
  average_score: null,
  description: '',
  school_year: 114,
  semester: 1,
  session_id: null
};

const form = ref({ ...defaultForm});

// 監聽 exam prop 變化
watch(() => props.exam, (exam) => {
  if (exam) {
    form.value = {
      name: exam.name,
      authority: exam.authority,
      type: exam.type,
      subject: exam.subject,
      grade: exam.grade,
      exam_date: formatDateForInput(exam.exam_date),
      full_score: exam.full_score,
      average_score: exam.average_score,
      description: exam.description || '',
      school_year: exam.school_year,
      semester: exam.semester,
      session_id: exam.session_id
    };
    
    // 如果是編輯模式且有關聯課程，設置選擇的課程
    if (exam.session_id && exam.session) {
      selectedCourseId.value = exam.session.course.course_id.toString();
    }
  } else {
    resetForm();
  }
}, { immediate: true });

// 第一部分：載入所有課程（當選擇補習班考試時）
const { 
  result: coursesResult, 
  loading: coursesLoading, 
  refetch: refetchCourses,
} = useQuery(GET_COURSES,
  undefined,
  () => ({ 
    enabled: computed(() => !!props.exam?.session_id || !!selectedCourseId.value || form.value.authority === 'MACADEMY'),
    fetchPolicy: 'cache-first' 
  })
);

const courses = computed(() => coursesResult.value?.courses.filter((course) => course.status === 'ONGOING') || []);

// 監聽載入狀態
watch(coursesLoading, (newVal) => {
  loadingCourses.value = newVal;
});

// 第二部分：當選擇課程時，載入該課程的時段
const { 
  result: sessionsResult, 
  loading: sessionsLoading, 
  refetch: refetchSessions,
} = useQuery(GET_COURSE_SESSIONS,
  ()=> ({ course_id: selectedCourseId.value }),
  { 
    enabled: computed(() => !!selectedCourseId.value),
    fetchPolicy: 'network-only' 
  }
);

watch(selectedCourseId, () => {
  // 切換課程時清空已選擇的時段
  form.value.session_id = null;
  refetchSessions();
});

const courseSessions = computed(() => sessionsResult.value?.courseSessions || []);

// 監聽載入狀態
watch(sessionsLoading, (newVal) => {
  loadingSessions.value = newVal;
});

// 監聽課程時段變化，自動選擇最新的時段
watch(courseSessions, (sessions) => {
  if (sessions.length > 0 && !form.value.session_id) {
    // 找到最新的課程時段（按開始時間排序）
    const latestSession = sessions.reduce((latest, session) => {
      return new Date(session.start_at) > new Date(latest.start_at) ? session : latest;
    }, sessions[0]);
    
    form.value.session_id = latestSession.session_id;
  }
});

const { mutate: createExam, loading: createLoading } = useMutation(CREATE_EXAM);
const { mutate: updateExam, loading: updateLoading } = useMutation(UPDATE_EXAM);

// 監聽載入狀態
watch([createLoading, updateLoading], ([create, update]) => {
  loading.value = create || update;
});

function resetForm() {
  form.value = {...defaultForm};
  selectedCourseId.value = '';
}

function formatDateForInput(dateTimeString) {
  if (!dateTimeString) return '';
  const date = new Date(dateTimeString);
  // return date.toISOString().split('T')[0];
  return date.toLocaleDateString('zh-TW', {
    timezone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).split('/').join('-');
}

function formatSessionDate(dateTimeString) {
  if (!dateTimeString) return '';
  return new Date(dateTimeString).toLocaleDateString('zh-TW');
}

function handleAuthorityChange() {
  if (form.value.authority === 'MACADEMY') {
    refetchCourses();
  } else {
    // 如果選擇非補習班考試，清空課程相關選擇
    selectedCourseId.value = '';
    form.value.session_id = null;
  }
}

function close() {
  resetForm();
  emit('close');
}

function submit() {
  // 驗證基本必填欄位
  if (!form.value.name || !form.value.authority || !form.value.type || !form.value.subject || !form.value.exam_date) {
    alert('請填寫所有必填欄位');
    return;
  }

  // 驗證補習班考試的課程選擇
  if (form.value.authority === 'MACADEMY') {
    if (!selectedCourseId.value) {
      alert('請選擇課程');
      return;
    }
    if (!form.value.session_id) {
      alert('請選擇課程時段');
      return;
    }
  }

  const examDate = form.value.exam_date ? new Date(form.value.exam_date + 'T12:00:00') : new Date();
  
  const input = {
    name: form.value.name,
    authority: form.value.authority,
    type: form.value.type,
    subject: form.value.subject,
    grade: form.value.grade? parseInt(form.value.grade) : null,
    exam_date: examDate.toISOString(),
    full_score: form.value.full_score ? parseFloat(form.value.full_score) : null,
    description: form.value.description,
    school_year: form.value.school_year ? parseInt(form.value.school_year) : null,
    semester: form.value.semester ? parseInt(form.value.semester) : null,
    session_id: form.value.authority === 'MACADEMY' ? form.value.session_id : null
  }

  if (props.isEdit) {
    updateExam({
      exam_id: props.exam.exam_id,
      input
    }).then(() => {
      emit('saved');
      resetForm();
    }).catch((error) => {
      console.error('更新考試失敗:', error);
      alert('更新考試失敗: ' + error.message);
    });
  } else {
    createExam({ input }).then(() => {
      emit('saved');
      resetForm();
    }).catch((error) => {
      console.error('創建考試失敗:', error);
      alert('創建考試失敗: ' + error.message);
    });
  }
}
</script>

<style scoped>
/* 樣式保持不變，與前一個版本相同 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
}

form {
  padding: 20px;
}

.form-section {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.form-section:last-of-type {
  border-bottom: none;
  margin-bottom: 0;
}

.section-title {
  margin: 0 0 20px 0;
  font-size: 1.2rem;
  color: var(--text-dark);
  padding-bottom: 10px;
  border-bottom: 2px solid var(--primary-color);
}

.form-group {
  margin-bottom: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
}

label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
}

input, select, textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-sizing: border-box;
  font-size: 0.9rem;
}

input:focus, select:focus, textarea:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
}

.helper-text {
  margin: 5px 0 0 0;
  font-size: 0.8rem;
  color: var(--text-light);
  font-style: italic;
}

.no-sessions-message {
  padding: 10px;
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 4px;
  color: #856404;
  text-align: center;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 30px;
}

.cancel-btn {
  padding: 10px 20px;
  background-color: #f5f5f5;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
}

.submit-btn {
  padding: 10px 20px;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
}

.submit-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.submit-btn:hover:not(:disabled) {
  background-color: var(--primary-hover);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
    gap: 0;
  }
  
  .modal-content {
    margin: 20px;
    max-height: calc(100vh - 40px);
  }
}
</style>